import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const EMPTY_POST = {
  title: "",
  slug: "",
  excerpt: "",
  content_html: "",
  cover_url: "",
  category: "Guide",
  tags: [],
  author_name: "Équipe DoctoSpa",
  meta_title: "",
  meta_description: "",
  published: false,
  reading_time_min: 4,
};

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null or post object
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog/posts");
      setItems(r.data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!editing) return;
    if (!editing.title || editing.title.length < 3) {
      toast.error("Le titre doit contenir au moins 3 caractères");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        tags: typeof editing.tags === "string"
          ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : editing.tags || [],
      };
      if (editing.id) {
        await api.put(`/admin/blog/posts/${editing.id}`, payload);
        toast.success("Article mis à jour");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Article créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="admin-blog-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Admin · Blog SEO</div>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Gestion du blog</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Publiez des articles éditoriaux pour l'acquisition SEO. Ils sont exposés sur <code>/blog</code>.
          </p>
        </div>
        <Button
          className="rounded-full"
          onClick={() => setEditing({ ...EMPTY_POST })}
          data-testid="admin-blog-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> Nouvel article
        </Button>
      </div>

      {editing && (
        <Card className="mt-8 p-6 border-black/10" data-testid="admin-blog-editor">
          <h2 className="font-display text-lg font-bold">{editing.id ? "Éditer l'article" : "Nouvel article"}</h2>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Field label="Titre" required>
              <Input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Ex: Erreur FLO sur spa Balboa"
                data-testid="admin-blog-title"
              />
            </Field>
            <Field label="Slug (URL)" hint="Auto-généré si vide">
              <Input
                value={editing.slug || ""}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                placeholder="erreur-flo-balboa"
                data-testid="admin-blog-slug"
              />
            </Field>
            <Field label="Catégorie">
              <Input
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                data-testid="admin-blog-category"
              />
            </Field>
            <Field label="Temps de lecture (min)">
              <Input
                type="number"
                min={1}
                max={60}
                value={editing.reading_time_min}
                onChange={(e) => setEditing({ ...editing, reading_time_min: Number(e.target.value) })}
                data-testid="admin-blog-reading-time"
              />
            </Field>
            <Field label="Auteur">
              <Input
                value={editing.author_name}
                onChange={(e) => setEditing({ ...editing, author_name: e.target.value })}
                data-testid="admin-blog-author"
              />
            </Field>
            <Field label="Cover URL">
              <Input
                value={editing.cover_url || ""}
                onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })}
                placeholder="https://…"
                data-testid="admin-blog-cover"
              />
            </Field>
            <Field label="Tags (séparés par des virgules)" full>
              <Input
                value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags}
                onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                placeholder="balboa, flo, pressostat"
                data-testid="admin-blog-tags"
              />
            </Field>
            <Field label="Extrait (résumé court)" full>
              <Textarea
                rows={2}
                value={editing.excerpt}
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                placeholder="Résumé de 1-2 phrases affiché dans les listes et cartes"
                data-testid="admin-blog-excerpt"
              />
            </Field>
            <Field label="Contenu (HTML)" full hint="Utilisez des balises HTML : <h2>, <p>, <ul>, <a href>, <strong>…">
              <Textarea
                rows={16}
                value={editing.content_html}
                onChange={(e) => setEditing({ ...editing, content_html: e.target.value })}
                className="font-mono text-xs"
                data-testid="admin-blog-content"
              />
            </Field>
            <Field label="Meta title (SEO)" full hint="Balise <title> spécifique — si vide, utilise le titre + | DoctoSpa">
              <Input
                value={editing.meta_title || ""}
                onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })}
                data-testid="admin-blog-meta-title"
              />
            </Field>
            <Field label="Meta description (SEO)" full hint="150-160 caractères, apparaît sur Google et OG">
              <Textarea
                rows={2}
                value={editing.meta_description || ""}
                onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })}
                data-testid="admin-blog-meta-desc"
              />
            </Field>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <Switch
              checked={editing.published}
              onCheckedChange={(v) => setEditing({ ...editing, published: v })}
              data-testid="admin-blog-published"
            />
            <span className="text-sm">{editing.published ? "Publié (visible publiquement)" : "Brouillon"}</span>
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              onClick={submit}
              disabled={saving}
              className="rounded-full"
              data-testid="admin-blog-save-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing.id ? "Enregistrer" : "Créer"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setEditing(null)}
              data-testid="admin-blog-cancel-btn"
            >
              Annuler
            </Button>
          </div>
        </Card>
      )}

      <Card className="mt-10 border-black/10 overflow-hidden" data-testid="admin-blog-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Aucun article.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Titre</th>
                <th className="text-left p-3">Catégorie</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-left p-3">Vues</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="p-3">
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-xs text-muted-foreground">/blog/{p.slug}</div>
                  </td>
                  <td className="p-3">{p.category}</td>
                  <td className="p-3">
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                        <Eye className="h-3 w-3" /> Publié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                        <EyeOff className="h-3 w-3" /> Brouillon
                      </span>
                    )}
                  </td>
                  <td className="p-3">{p.views || 0}</td>
                  <td className="p-3 text-right">
                    {p.published && (
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-black/5"
                        data-testid={`admin-blog-view-${p.slug}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                    <button
                      onClick={() => setEditing({ ...p, tags: p.tags || [] })}
                      className="inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-black/5"
                      data-testid={`admin-blog-edit-${p.slug}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => remove(p.id)}
                      className="inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-black/5 text-destructive"
                      data-testid={`admin-blog-delete-${p.slug}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Field({ label, hint, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs font-semibold">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
